import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';

const UserProtocolStatusContext = createContext(null);

/**
 * Single source of truth for the current user's protocol statuses (active + staged).
 * Avoids per-protocol protocols_status requests when checking "does user have this protocol?"
 */
export function UserProtocolStatusProvider({ children }) {
  const [statuses, setStatuses] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data: { session } = {} } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user?.id || typeof user.id !== 'string' || user.id === 'undefined' || user.id === 'null') {
        setStatuses([]);
        setIsLoading(false);
        return [];
      }

      const { data, error } = await supabase
        .from('protocols_status')
        .select('id, protocol_id, is_active')
        .eq('user_id', user.id);

      if (error) {
        setStatuses([]);
        return [];
      }

      const rows = data || [];
      setStatuses(rows);
      return rows;
    } catch (err) {
      setStatuses([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    refresh();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refresh]);

  const value = useMemo(() => {
    const statusMap = new Map();
    (statuses || []).forEach((s) => {
      if (s?.protocol_id) statusMap.set(s.protocol_id, { id: s.id, is_active: s.is_active });
    });

    const hasProtocol = (protocolId) => protocolId && statusMap.has(protocolId);
    const getStatusForProtocol = (protocolId) => (protocolId ? statusMap.get(protocolId) ?? null : null);
    const getActiveProtocolIds = () =>
      (statuses || []).filter((s) => s.is_active === true || s.is_active === 'true').map((s) => s.protocol_id);

    return {
      statuses: statuses || [],
      isLoading,
      hasProtocol,
      getStatusForProtocol,
      getActiveProtocolIds,
      refresh,
    };
  }, [statuses, isLoading, refresh]);

  return (
    <UserProtocolStatusContext.Provider value={value}>
      {children}
    </UserProtocolStatusContext.Provider>
  );
}

export function useUserProtocolStatus() {
  const ctx = useContext(UserProtocolStatusContext);
  if (!ctx) {
    return {
      statuses: [],
      isLoading: false,
      hasProtocol: () => false,
      getStatusForProtocol: () => null,
      getActiveProtocolIds: () => [],
      refresh: async () => [],
    };
  }
  return ctx;
}
